import React, { useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as ChevronRight } from '../../assets/Breadcrumb/chevron-right.svg';
import './Breadcrumb.css';

const DRAG_THRESHOLD_PX = 8;

const Breadcrumb = ({ items = [], isHero = false }) => {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startScrollLeft: 0, didMove: false, active: false });

  const handleMouseDown = useCallback((e) => {
    if (!scrollRef.current) return;
    if (e.target.closest('a')) return;
    dragRef.current = {
      startX: e.clientX,
      startScrollLeft: scrollRef.current.scrollLeft,
      didMove: false,
      active: true
    };
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!scrollRef.current || !dragRef.current.active) return;
    const { startX, startScrollLeft } = dragRef.current;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX) {
      if (!dragRef.current.didMove) dragRef.current.didMove = true;
      scrollRef.current.scrollLeft = startScrollLeft + dx;
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current.active = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);


  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`c-breadcrumb-wrapper ${isHero ? 'single-hero' : ''}`}>
      <div className="c-breadcrumb-container content">
        <div
          className={`c-breadcrumb ${isDragging ? 'is-dragging' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <nav aria-label="Breadcrumb">
            <ol
              ref={scrollRef}
              className="breadcrumb"
              itemScope
              itemType="https://schema.org/BreadcrumbList"
            >
              {items.map((item, index) => {
                const isLast = index === items.length - 1;
                const position = index + 1;

                return (
                  <React.Fragment key={index}>
                    <li
                      className="breadcrumb-item"
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      {isLast ? (
                        <span itemProp="name">{item.label}</span>
                      ) : (
                        <a
                          href={item.url}
                          itemProp="item"
                          onClick={(e) => {
                            if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                              e.preventDefault();
                              window.location.href = item.url;
                            }
                          }}
                        >
                          <span itemProp="name">{item.label}</span>
                        </a>
                      )}
                      <meta itemProp="position" content={String(position)} />
                    </li>
                    {!isLast && (
                      <li className="breadcrumb-separator" aria-hidden="true">
                        <ChevronRight />
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>
    </div>
  );
};

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string
    })
  ),
  isHero: PropTypes.bool
};

export default Breadcrumb;

